import { useMutation, useQuery } from "@tanstack/react-query";

import {
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Table,
  Typography,
  message,
} from "antd";

import { useState } from "react";
import AudioPlayer from "../components/AudioPlayer";
import BackendUpload from "../components/BackendUpload";
import Player from "../components/Player";
import Remove from "../components/Remove";
import useSearch from "../components/Search";
import {
  addMusic,
  deleteMusic,
  getAllArtists,
  getAllSubscriptions,
  getGenres,
  getImage,
  getMusic,
  updateMusic,
} from "../utils/api";

const Music = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["musics"],
    queryFn: async () => await getMusic(),
  });
  const { data: subs } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: async () => await getAllSubscriptions(),
  });
  const { mutate, isLoading: isLoading2 } = useMutation(
    async (values) => await deleteMusic(values),
    {
      onSuccess: () => {
        messageApi.success(`Deleted`);
        refetch();
      },
    }
  );

  const { data: categories } = useQuery({
    queryKey: ["genres"],
    queryFn: async () => await getGenres(),
  });
  const { data: artists } = useQuery({
    queryKey: ["artists"],
    queryFn: async () => await getAllArtists(),
  });

  const search = useSearch();

  const columns = [
    {
      title: "S.no",
      dataIndex: "sno",

      render(value, record, index) {
        return index + 1;
      },
    },
    {
      title: "Actions",
      dataIndex: "action",
      render(value, record) {
        return (
          <div>
            <AddData
              refech={refetch}
              update={record}
              categories={categories}
              artists={artists}
            />
            <Remove
              id={record._id}
              type="Music"
              refech={refetch}
              subs={subs?.filter((item) =>
                record?.subscriptions?.includes(item._id)
              )}
            />
            <Popconfirm
              title="Delete the task"
              description="Are you sure to delete this task?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => mutate(record._id)}
            >
              <Button
                danger
                style={{ marginTop: 10, width: "100%" }}
                loading={isLoading2}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },

    {
      title: "Cover",
      dataIndex: "cover",
      render(value, record) {
        return (
          <Image
            src={record.cover}
            style={{
              width: 50,
              height: 50,
              borderRadius: 5,
            }}
          />
        );
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      ...search("name"),
    },
    {
      title: "Release Year",
      dataIndex: "releaseYear",
    },
    {
      title: "Audio",
      dataIndex: "audio",
      render(value, record) {
        return <AudioPlayer link={record.audio} />;
      },
    },
    {
      title: "View",
      dataIndex: "video",
      render(value, record) {
        return <Player link={record.video} />;
      },
    },

    {
      title: "Genres",
      dataIndex: ["genres"],
      render(value, record) {
        return value?.map((item) => item.name).join(", ");
      },
    },
  ];
  return (
    <div
      style={{
        padding: 24,
      }}
    >
      {contextHolder}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Typography style={{ fontSize: 20, fontWeight: 600, marginBottom: 10 }}>
          Music
        </Typography>

        <div>
          <AddData refech={refetch} categories={categories} artists={artists} />
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={isLoading}
        scroll={{ x: 1500 }}
      />
    </div>
  );
};

export default Music;

const AddData = ({
  refech,

  update,
  categories,
  artists,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const { mutate, isLoading } = useMutation(
    async (values) =>
      update ? await updateMusic(update._id, values) : await addMusic(values),
    {
      onSuccess: () => {
        messageApi.success(`Added`);
        //reset form
        !update && form.resetFields();
        refech();
        setVisible(false);
      },
      onError: (err) => {
        messageApi.error(err?.response?.data?.message);
      },
    }
  );
  const type = Form.useWatch("type", form);
  const videoType = Form.useWatch("videoType", form);
  return (
    <div>
      {contextHolder}
      <Button
        type="primary"
        onClick={() => {
          setVisible(true);
        }}
        style={{
          width: "100%",
        }}
      >
        {update ? "Update" : "Add"}
      </Button>
      <Modal
        title={update ? "Update Music" : "Add Music"}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          labelCol={{ span: 9 }}
          layout="horizontal"
          style={{
            maxWidth: 600,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          initialValues={
            update && {
              name: update.name,
              releaseYear: update.releaseYear,

              type: update.isFree ? true : false,

              videoType: update.videoType,
              video: update.video,
              price: update.price,
              validity: update.validity,
              genres: update.genres.map((item) => item._id),
              artist: update.artist.map((item) => item._id),
              featuringArtist: update.featuringArtist.map((item) => item._id),
            }
          }
          onFinish={(values) => {
            mutate({
              ...values,
              isPaid: values?.type ? false : true,
              isFree: values?.type ? true : false,
              audio: getImage(values?.audio) ?? update?.audio,
              cover: getImage(values?.cover) ?? update?.cover,
              price: values.price ?? 2,
              validity: values.validity ?? 4,
              video: getImage(values?.video) ?? update?.video,
            });
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Release Year"
            name="releaseYear"
            rules={[{ required: true, message: "Release Year is required" }]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          <BackendUpload
            label={"Audio"}
            type={"audios"}
            name={"audio"}
            update={update}
          />
          <BackendUpload
            label={"Cover"}
            type={"photos"}
            name={"cover"}
            update={update}
          />

          <Form.Item
            label="Video Type "
            name="videoType"
            rules={[{ required: true, message: "videoType  is required" }]}
          >
            <Select placeholder="Select Video Type ">
              {["Youtube", "Video"].map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {videoType === "Youtube" ? (
            <Form.Item
              label="Video"
              name="video"
              rules={[{ required: true, message: "Video is required" }]}
            >
              <Input />
            </Form.Item>
          ) : (
            <BackendUpload
              label={"Video"}
              type={"videos"}
              name={"video"}
              update={update}
            />
          )}

          <Form.Item
            label="Genres"
            name="genres"
            rules={[{ required: true, message: "Genres is required" }]}
          >
            <Select mode="multiple" placeholder="Select Genres">
              {categories?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Type" name="type" required>
            <Switch checkedChildren="Free" unCheckedChildren="Paid" />
          </Form.Item>
          {!type && (
            <>
              <Form.Item label="Price" name="price" required>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Validity" name="validity" required>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Artist"
            name="artist"
            rules={[{ required: true, message: "Artist is required" }]}
          >
            <Select placeholder="Select Artist" mode="multiple">
              {artists?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Featuring Artist"
            name="featuringArtist"
            rules={[{ required: true, message: "Artist is required" }]}
          >
            <Select placeholder="Select Artist" mode="multiple">
              {artists?.map((item) => (
                <Select.Option key={item._id} value={item._id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={isLoading}>
            Submit
          </Button>
        </Form>
      </Modal>
    </div>
  );
};
